import { FormEvent, useState } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { setPassword as apiSetPassword } from '../api/auth';

/**
 * Écran de création du mot de passe après une première connexion par lien
 * magique. Le moniteur peut aussi passer cette étape et la faire plus tard.
 */
export default function SetPasswordScreen() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const skip = () => {
    // On marque localement l'étape comme faite pour ne pas la re-proposer
    // pendant la session (le serveur reste la source de vérité).
    if (user) setUser({ ...user, passwordChosen: true });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setBusy(true);
    try {
      await apiSetPassword(password);
      if (user) setUser({ ...user, passwordChosen: true });
    } catch {
      setError('Impossible de définir le mot de passe. Réessayez.');
    } finally {
      setBusy(false);
    }
  };

  // Création du mot de passe (refonte du 14/10/2026) : habillage de la charte, même parcours.
  return (
    <div className="flex min-h-screen items-center justify-center bg-hero-indigo px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-white text-primary shadow-card">
            <ShieldCheck size={24} />
          </div>
          <h1 className="text-center text-title-md text-heading">
            Créez votre mot de passe
          </h1>
          <p className="mt-2 text-center text-sm leading-relaxed text-secondary">
            Pour vos prochaines connexions sans lien e-mail.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-card bg-white p-6 shadow-card sm:p-8"
        >
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-body">
              Nouveau mot de passe
            </label>
            <div className="relative">
              <Lock
                size={16}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
              />
              <input
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 w-full rounded-xl border border-line bg-surface-light pl-10 pr-3 text-[15px] text-body outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
                placeholder="Au moins 8 caractères"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-body">
              Confirmez le mot de passe
            </label>
            <div className="relative">
              <Lock
                size={16}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
              />
              <input
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-11 w-full rounded-xl border border-line bg-surface-light pl-10 pr-3 text-[15px] text-body outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div role="alert" className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-pill bg-btn-primary px-4 text-sm font-semibold text-white shadow-btn transition-all enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-hover disabled:opacity-60"
          >
            {busy ? 'Enregistrement…' : 'Enregistrer mon mot de passe'}
          </button>
          <button
            type="button"
            onClick={skip}
            className="min-h-[40px] w-full text-center text-sm font-medium text-secondary hover:text-body"
          >
            Plus tard
          </button>
        </form>
      </div>
    </div>
  );
}
